import { type CsticId, FullCsticIdFromResolvedDefinition, SimpleCsticId } from "~/features/configurator/Linear/models";
import LabeledInput from "~/common/components/LabeledInput";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import { ActionIconButton } from "~/common/components/Button/ActionIconButton";
import { Stack, Tab } from "~/common/mui";
import EditIcon from "@mui/icons-material/Edit";

import classNames from "classnames";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/common/mui/Dialog/Dialog";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { SimpleCsticTileField } from "~/features/configurator/Shaft/components/SimpleCsticTileField";
import { LinearTileV2 } from "~/features/configurator/Shaft/substeps/LinearTileV2";
import { useCsticSelectProps } from "~/features/configurator/Shaft/components/useCsticSelectProps";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";

import { useEffect, useRef } from "react";
import type { ResolvedCsticDefinition } from "~/features/configurator/common/v2/StepDefinition";
import { FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE } from "~/features/configurator/Carriage/carriageConfiguratorConstants";

interface Props {
  className?: string;
  cstic: ResolvedCsticDefinition;
  tabsCstic?: ResolvedCsticDefinition;
  substepType: SubstepType;
  showEditButton?: boolean;
}

export const SimpleCsticSelectPopup = ({
  cstic: cstic,
  tabsCstic: tabsCstic,
  className,
  substepType,
  showEditButton,
}: Props) => {
  const { val: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const csticId = FullCsticIdFromResolvedDefinition(cstic);

  const {
    cstic: lubricationConnectorCstic,
    selectProps: { error },
  } = useCsticSelectProps({
    csticId,
    substep: substepType,
    required: cstic.required,
  });

  const {
    selectProps: { onChange: setActiveTab, value: activeTab },
    csticValues: tabs,
  } = useCsticSelectProps({
    csticId: tabsCstic ? FullCsticIdFromResolvedDefinition(tabsCstic) : SimpleCsticId("N/A"),
    substep: substepType,
    csticsToSet:
      tabsCstic?.name === FAKE_CSTIC_LUBRICATION_CONNECTOR_TYPE
        ? [{ key: lubricationConnectorCstic.key, value: lubricationConnectorCstic.value ?? null }]
        : [],
  });

  const lastActiveTab = useRef<string | null>(activeTab);
  useEffect(() => {
    if (isModalOpen) {
      lastActiveTab.current = activeTab;
    }
  }, [isModalOpen]);

  const onCloseModal = (reason?: "close" | "valueSelected") => {
    closeModal();
    if (reason === "close") {
      setActiveTab(lastActiveTab.current!);
    }
  };

  const selectedConnector = lubricationConnectorCstic.value;
  const selectedValue = lubricationConnectorCstic.values.find((val) => val.value === selectedConnector);
  const selectedValueLabel = selectedValue?.label ?? "";

  const label = lubricationConnectorCstic.label || "MISSING LABEL";

  const spanClasses = classNames("scha__lc__external-label", {
    "f-disabled-text": cstic.disabled,
  });

  return (
    <>
      <div className={className}>
        <LabeledInput
          label={<LanguageAwareExternalContent content={label} className={"scha__lc__external-label"} />}
          hasError={error}
          info={lubricationConnectorCstic.info}
        >
          <div className={"title-medium mt-1 d-flex flex-align-center"}>
            {selectedValue?.standard && <LinearTileV2.StandardIndicator omitText className="icon-margin" />}
            <span className={"icon-margin"}>
              <LanguageAwareExternalContent content={selectedValueLabel} className={spanClasses} />
            </span>
            {showEditButton && (
              <ActionIconButton
                icon={<EditIcon sx={{ fontSize: 20 }} />}
                onClick={openModal}
                disabled={cstic.disabled}
              />
            )}
          </div>
        </LabeledInput>
      </div>
      {isModalOpen && (
        <SelectionModal
          onClose={onCloseModal}
          title={label}
          description={lubricationConnectorCstic.description}
          activeTab={activeTab ?? ""}
          onTabChange={setActiveTab}
          csticId={csticId}
          tabs={tabs}
          substepType={substepType}
        />
      )}
    </>
  );
};

type TabData = {
  value: string;
  label: string;
};

interface SelectionModalProps {
  onClose: (reason?: "close" | "valueSelected") => void;
  title: string;
  description?: string;
  tabs: TabData[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  csticId: CsticId;
  substepType: SubstepType;
}

function SelectionModal({
  onClose,
  title,
  description,
  tabs,
  activeTab,
  onTabChange,
  csticId,
  substepType,
}: SelectionModalProps) {
  const xmlT = useXmlConfiguratorTranslations().t!;
  return (
    <Dialog open={true} onClose={() => onClose("close")} maxWidth="desktop-m">
      <DialogHeader onClose={() => onClose("close")}>
        <DialogTitle sx={{ p: 0 }}>
          <LanguageAwareExternalContent content={title} className={"scha__lc__external-label"} />
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Stack gap={2}>
          {description && <LanguageAwareExternalContent content={description} className={"scha__lc__external-label"} />}
          {tabs.length > 0 && (
            <Tabs onChange={(e, val) => onTabChange(val)} variant="fullWidth" value={activeTab}>
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  value={tab.value}
                  label={<LanguageAwareExternalContent content={tab.label} className={"scha__lc__external-label"} />}
                />
              ))}
            </Tabs>
          )}

          <SimpleCsticTileField
            csticId={csticId}
            tilesPerRow={4}
            substepType={substepType}
            renderTile={({ onClick, disabled, selected, value }) => (
              <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
                <LinearTileV2.Image src={xmlT(value.imageUrl)} ratio="" />
                <LinearTileV2.CheckedIndicator />
                <p className="m-0">{value.value}</p>
                {value.standard && <LinearTileV2.StandardIndicator />}
              </LinearTileV2.Base>
            )}
            onClick={() => onClose("valueSelected")}
          />
        </Stack>
        {/* 
        {tiles.length > 0 && (
          <Grid container columns={4} spacing={"var(--gutter)"}>
            {tiles.map((tile, index) => (
              <Grid key={index} size={1}>
                <LinearTile
                  key={tile.value}
                  index={index}
                  isStandard={tile.standard}
                  imageUrl={tile.imageUrl}
                  onClickHandler={() => {
                    onTileClick(tile);
                  }}
                  info={tile.info}
                  isPicked={tile.value === activeTile}
                  title={tile.label}
                />
              </Grid>
            ))}
          </Grid>
        )} */}
      </DialogContent>
    </Dialog>
  );
}
